import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bank, MagnifyingGlass, PencilSimple, ClockCounterClockwise, CurrencyInr } from "@phosphor-icons/react";
import { toast } from "sonner";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

export default function ServiceRates() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("all");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState([]);
  const [breakdown, setBreakdown] = useState(null);

  useEffect(() => {
    api.get("/services").then((r) => setRows(r.data)).catch(() => {});
    api.get("/analytics/collection-breakdown").then((r) => setBreakdown(r.data)).catch(() => {});
  }, []);

  const departments = useMemo(() => {
    const s = new Set(rows.map((r) => r.department).filter(Boolean));
    return ["all", ...Array.from(s).sort()];
  }, [rows]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (dept !== "all" && r.department !== dept) return false;
    if (q && !`${r.name} ${r.department} ${r.category}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [rows, q, dept]);

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      default_govt_fee: r.default_govt_fee || 0,
      default_service_charge: r.default_service_charge || 0,
      gst_applicable: !!r.gst_applicable,
      gst_percent: r.gst_percent || 18,
      active: r.active !== false,
      effective_from: new Date().toISOString().slice(0, 10),
      change_note: "",
    });
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.patch(`/services/${editing.id}/rate`, {
        ...form,
        default_govt_fee: Number(form.default_govt_fee) || 0,
        default_service_charge: Number(form.default_service_charge) || 0,
        gst_percent: Number(form.gst_percent) || 18,
      });
      toast.success("Rate updated");
      const r = await api.get("/services");
      setRows(r.data);
      setEditing(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const openHistory = async (r) => {
    setHistoryFor(r);
    try {
      const { data } = await api.get(`/services/${r.id}/rate-history`);
      setHistory(data);
    } catch { setHistory([]); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1500px] space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-amber-600 font-semibold mb-2 flex items-center gap-2">
          <Bank size={14} weight="fill" /> Rate Master
        </div>
        <h1 className="text-3xl lg:text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Government Fees &amp; Service Charges</h1>
        <p className="text-sm text-slate-600 mt-1.5">Every service has an authoritative fee. Only accountants and admins can edit — every change is versioned.</p>
      </div>

      {/* Collection breakdown */}
      {breakdown && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="rate-breakdown-row">
          <BdCard label="Today · Govt Fee" value={inr(breakdown.today.government_fee)} tone="cyan" />
          <BdCard label="Today · Service Charge" value={inr(breakdown.today.service_charge)} tone="indigo" />
          <BdCard label="Today · Add-ons" value={inr(breakdown.today.add_ons)} tone="amber" />
          <BdCard label="Today · Total" value={inr(breakdown.today.total_paid)} tone="emerald" big />
        </div>
      )}

      <Card className="p-4 bg-white">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="relative flex-1 min-w-[240px]">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="rate-search" placeholder="Search service, department, category…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-9 text-sm" />
          </div>
          <Select value={dept} onValueChange={setDept}>
            <SelectTrigger className="h-9 w-[200px] text-sm" data-testid="rate-dept"><SelectValue placeholder="Department" /></SelectTrigger>
            <SelectContent>{departments.map((d) => <SelectItem key={d} value={d}>{d === "all" ? "All departments" : d}</SelectItem>)}</SelectContent>
          </Select>
          <div className="text-xs text-slate-500 ml-auto">{filtered.length} of {rows.length}</div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-200">
              <tr>
                <th className="text-left py-2 pr-3">Service</th>
                <th className="text-left py-2 pr-3">Department</th>
                <th className="text-right py-2 pr-3">Govt Fee</th>
                <th className="text-right py-2 pr-3">Service Charge</th>
                <th className="text-right py-2 pr-3">Total</th>
                <th className="text-center py-2 pr-3">GST</th>
                <th className="text-center py-2 pr-3">Active</th>
                <th className="text-right py-2 pr-1">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const total = (r.default_govt_fee || 0) + (r.default_service_charge || 0);
                return (
                  <tr key={r.id} data-testid={`rate-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 pr-3 font-medium text-slate-800">{r.name}</td>
                    <td className="py-2 pr-3 text-xs text-slate-600">{r.department || <span className="text-slate-400">—</span>}</td>
                    <td className="py-2 pr-3 text-right font-mono text-slate-700">{inr(r.default_govt_fee)}</td>
                    <td className="py-2 pr-3 text-right font-mono text-slate-700">{inr(r.default_service_charge)}</td>
                    <td className="py-2 pr-3 text-right font-mono font-semibold text-slate-900">{inr(total)}</td>
                    <td className="py-2 pr-3 text-center">
                      {r.gst_applicable ? (
                        <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700">{r.gst_percent}%</Badge>
                      ) : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                    <td className="py-2 pr-3 text-center">
                      {r.active !== false
                        ? <span className="text-emerald-600 text-xs">●</span>
                        : <span className="text-slate-300 text-xs">○</span>}
                    </td>
                    <td className="py-2 pr-1 text-right whitespace-nowrap">
                      <button onClick={() => openHistory(r)} data-testid={`rate-history-${r.id}`} className="text-slate-500 hover:bg-slate-100 p-1 rounded"><ClockCounterClockwise size={14} weight="bold" /></button>
                      <button onClick={() => openEdit(r)} data-testid={`rate-edit-${r.id}`} className="text-indigo-600 hover:bg-indigo-50 p-1 rounded ml-1"><PencilSimple size={14} weight="bold" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle>Update Rate — {editing?.name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Government Fee (₹) *"><Input type="number" value={form.default_govt_fee} onChange={(e) => setForm({ ...form, default_govt_fee: e.target.value })} data-testid="rate-govt" /></Field>
            <Field label="Service Charge (₹) *"><Input type="number" value={form.default_service_charge} onChange={(e) => setForm({ ...form, default_service_charge: e.target.value })} data-testid="rate-svc" /></Field>
            <div className="col-span-2 flex items-center gap-3 border-t border-slate-100 pt-2">
              <Checkbox id="rate-gst" checked={form.gst_applicable} onCheckedChange={(v) => setForm({ ...form, gst_applicable: !!v })} data-testid="rate-gst-cb" />
              <label htmlFor="rate-gst" className="text-sm font-medium">GST Applicable</label>
              {form.gst_applicable && (
                <>
                  <Input type="number" className="h-8 w-20" value={form.gst_percent} onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} data-testid="rate-gst-pct" />
                  <span className="text-xs text-slate-500">%</span>
                </>
              )}
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <Checkbox id="rate-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: !!v })} />
              <label htmlFor="rate-active" className="text-sm">Active in catalog</label>
            </div>
            <Field label="Effective From"><Input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} /></Field>
            <Field label="Reason / Note (kept in history)" full>
              <Textarea rows={2} value={form.change_note} onChange={(e) => setForm({ ...form, change_note: e.target.value })} placeholder="e.g. Govt notification 15-Feb-2026" data-testid="rate-note" />
            </Field>
          </div>
          <div className="rounded bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-900">
            Old bills already generated will keep their original rates — only new applications use these.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="rate-save-btn">
              {saving ? "Saving…" : "Save Rate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!historyFor} onOpenChange={(v) => !v && setHistoryFor(null)}>
        <DialogContent className="max-w-2xl bg-white max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Rate History — {historyFor?.name}</DialogTitle></DialogHeader>
          {history.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">No rate changes recorded yet.</div>
          ) : (
            <div className="space-y-2">
              {history.map((h) => (
                <Card key={h.id} className="p-3 bg-slate-50/70 border-slate-200">
                  <div className="flex items-center justify-between mb-1.5 text-xs text-slate-500">
                    <span>{new Date(h.changed_at).toLocaleString()} · {h.changed_by}</span>
                    <span className="italic">effective {h.effective_from ? h.effective_from.slice(0, 10) : "—"}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <RateDiff label="Govt Fee" prev={h.prev_default_govt_fee} next={h.new_default_govt_fee} />
                    <RateDiff label="Service Charge" prev={h.prev_default_service_charge} next={h.new_default_service_charge} />
                    <RateDiff label="GST %" prev={h.prev_gst_applicable ? `${h.prev_gst_percent}%` : "off"} next={h.new_gst_applicable ? `${h.new_gst_percent}%` : "off"} plain />
                  </div>
                  {h.change_note && <div className="mt-2 text-xs text-slate-700 italic">"{h.change_note}"</div>}
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">{label}</div>
      {children}
    </div>
  );
}

function RateDiff({ label, prev, next, plain }) {
  const changed = String(prev) !== String(next);
  return (
    <div className="rounded p-2 bg-white border border-slate-200">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">{label}</div>
      <div className="flex items-center gap-1 font-mono text-slate-700">
        <span className="line-through text-slate-400">{plain ? prev : "₹" + prev}</span>
        <span className={changed ? "text-amber-700 font-bold" : "text-slate-500"}>→ {plain ? next : "₹" + next}</span>
      </div>
    </div>
  );
}

function BdCard({ label, value, tone, big }) {
  const tones = {
    cyan: "border-l-cyan-500 bg-cyan-50/40",
    indigo: "border-l-indigo-500 bg-indigo-50/40",
    amber: "border-l-amber-500 bg-amber-50/40",
    emerald: "border-l-emerald-500 bg-emerald-50/40",
  };
  return (
    <Card className={`p-3 border-l-4 ${tones[tone] || tones.indigo}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</div>
      <div className={`${big ? "text-2xl" : "text-xl"} font-bold text-slate-900 mt-0.5`} style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
    </Card>
  );
}
